import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  ViewChild,
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { CartItemFacade } from '@features/cart-item/facades/cart-item-facade/cart-item.facade';
import { PointsAccountFacade } from '@features/points-account/facades/points-account/points-account.facade';
import { PointsSummaryFacade } from '@features/points-summary/facades/points-summary-facade/points-summary.facade';
import { UserPreferenceFacade } from '@features/user-preference/facades/user-preference/user-preference.facade';
import { Store } from '@ngrx/store';
import { AuthFacade } from '@rd/core/auth/store/facades/auth.facade';
import { LoyaltyCurrencyFacade } from '@rd/core/facades/loyalty-currency-facade/loyalty-currency.facade';
import { DomService } from '@rd/core/services/dom/dom.service';
import { GuardhouseService } from '@rd/core/services/guardhouse/guardhouse.service';
import { PointsNumberService } from '@rd/core/services/points-number/points-number.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { BRAND_NAME } from '@rd/core/tokens/rd-lib.token';
import { PointsSummaryHelper } from '@rd/helpers/points-summary.helper';
import {
  FeatureFlags,
  GuardHouseLogoutCodes,
  LoyaltyCurrency,
  PointsAccount,
  PointsSummary,
  UserPreference,
} from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { AnzTooltipsComponent } from '@rd/wl/anz/components/tooltips/tooltips.component';
import { ANZFeatureFlagService } from '@rd/wl/anz/services/anz-feature-flag.service';
import { AnzPointsExpiryService } from '@rd/wl/anz/services/expiry-date/points-expiry.service';
import * as CSS from 'csstype';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMapTo } from 'rxjs/operators';

type UserAction = 'login' | 'logout' | 'enroll';

@Component({
  selector: 'rd-anz-mobile-header',
  templateUrl: './mobile-header.component.html',
  styleUrls: ['./mobile-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnzMobileHeaderComponent {
  @HostBinding('class.rd-anz-mobile-header') componentClass = true;

  @ViewChild('sidenav') sideNavRef: MatSidenav;
  sideNavOpenEvent: Subject<void> = new Subject<void>();

  @ViewChild('expiryPointsTooltips')
  expiryPointsTooltipsRef: AnzTooltipsComponent;

  @ViewChild('expiryPointsButton') expiryPointsButton: ElementRef;

  @Input() cardSelectorDisabled = false;

  isSideNavOpen = false;
  hideSideNavElement = true;

  isLoggedIn$: Observable<boolean> = this.authFacade.isLoggedIn$;

  loyaltyCurrency$: Observable<LoyaltyCurrency | null> =
    this.loyaltyCurrencyFacade.getLoyaltyCurrencySelected$.pipe(
      filter(isNotNull),
    );

  pointsAccountId$: Observable<string | undefined> =
    this.userPreferenceFacade.userPreferences$.pipe(
      map((userPreference: UserPreference) => userPreference.pointsAccountId),
    );

  activePointsAccount$: Observable<PointsAccount | undefined> = combineLatest([
    this.pointsAccountFacade.pointsAccounts$,
    this.pointsAccountId$,
  ]).pipe(
    map(([pointsAccounts, id]) =>
      pointsAccounts.find((account) => account.id === id),
    ),
  );

  isPointsNavShown$ = combineLatest([
    this.activePointsAccount$,
    this.pointsSummaryFacade.pointsSummary$,
    this.isLoggedIn$,
  ]).pipe(map((values) => values.reduce((acc, val) => acc && !!val, true)));

  pointsSummary$: Observable<PointsSummary | null> =
    this.pointsSummaryFacade.pointsSummary$.pipe(
      filter(isNotNull),
      map((pointsSummary: PointsSummary) => pointsSummary),
    );

  expiryPoints$: Observable<string | undefined> = this.isLoggedIn$.pipe(
    filter((isLoggedIn: boolean) => !!isLoggedIn),
    switchMapTo(
      this.pointsSummary$.pipe(filter(isNotNull), distinctUntilChanged()),
    ),
    map((activePointsSummary: PointsSummary) => {
      const expiryPoints = this.pointsNumberService.roundNumber(
        this.pointsSummaryHelper.expiringPointsAtDate(
          activePointsSummary,
          this.pointsExpiryService.lastDayInExpiryYear(),
        ),
      );

      return expiryPoints !== '0' ? expiryPoints : '';
    }),
  );

  readonly shoppingCartFeatureFlag = FeatureFlags.ShoppingCart;

  constructor(
    public cartItemFacade: CartItemFacade,
    private urlUtilsService: UrlUtilsService,
    private domService: DomService,
    private guardhouseService: GuardhouseService,
    private pointsExpiryService: AnzPointsExpiryService,
    private store: Store,
    public pointsAccountFacade: PointsAccountFacade,
    public pointsSummaryFacade: PointsSummaryFacade,
    public pointsNumberService: PointsNumberService,
    public pointsSummaryHelper: PointsSummaryHelper,
    private userPreferenceFacade: UserPreferenceFacade,
    private loyaltyCurrencyFacade: LoyaltyCurrencyFacade,
    private authFacade: AuthFacade,
    public featureFlagService: ANZFeatureFlagService,
    @Inject(BRAND_NAME) public brandName: 'ANZ-BIZ' | 'ANZ',
  ) {
    this.expiryPoints$.subscribe((expiryPoints: string | undefined) => {
      if (!!expiryPoints) {
        this.showExpiryPointsTooltip();
      } else {
        this.closeExpiryPointsTooltip();
      }
    });
  }

  get logoImageUrl(): string {
    return this.urlUtilsService.imageUrl('/images/rd/header-logo.png');
  }

  get lastDayInYear(): string {
    return this.pointsExpiryService.lastDayInExpiryYearStr();
  }

  openSideNav(): void {
    this.isSideNavOpen = true;
    this.sideNavRef.open();
    this.sideNavOpenEvent.next();
  }

  closeSideNav(): void {
    this.isSideNavOpen = false;
    this.sideNavRef.close();
  }

  handleSideNavOpen(): void {
    this.domService.lockBody();
  }

  handleSideNavClose(): void {
    this.domService.unlockBody();
  }

  style(): CSS.Properties {
    if (!this.isSideNavOpen) {
      return {
        pointerEvents: 'none',
        display: this.hideSideNavElement ? 'none' : 'block',
      };
    }
    return {};
  }

  handleSideNavOpenChange(): void {
    this.hideSideNavElement = !this.isSideNavOpen;
  }

  handleUserAction(action: UserAction): void {
    switch (action) {
      case 'enroll': {
        return this.guardhouseService.redirectToEnrollUrl();
      }
      case 'login': {
        return this.guardhouseService.redirectToLoginUrl();
      }
      case 'logout': {
        this.guardhouseService.redirectToLogoutUrl(
          GuardHouseLogoutCodes.IntentionalLogout,
        );
      }
    }
  }

  showExpiryPointsTooltip(): void {
    this.expiryPointsTooltipsRef?.open();
  }

  closeExpiryPointsTooltip(): void {
    this.expiryPointsTooltipsRef?.close();
  }

  onTooltipsVisibilityChanged(): void {
    this.expiryPointsButton.nativeElement.focus();
  }
}
